import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import RadioCardsGroup from '@ui-components-3/ui/lib/components/RadioCardsGroup'
import * as ProviderSelectors from 'redux/selectors/providers'
import withForm from 'hoc/withForm'
import ProviderHeader from 'components/visit-creation/provider-info/ProviderHeader'
import TestIds from 'util/TestIds'
import FilterIconUrl from 'assets/icons/ic_filter.svg'
import ResetIconUrl from 'assets/icons/ic_reset.svg'
import TherapyProviderFilters from '../TherapyProviderFilters/TherapyProviderFilters'
import { FiltersSchema } from 'schemas/NewVisitProviderSchema'
import {
  getPrintableShortProviderName,
  isTherapyRelated,
  normalizeFiltersForApi,
  normalizePreferencesForApi,
  providerDefaultFilters,
} from 'util/provider'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import NonTherapyProviderFilters from '../NonTherapyProviderFilters'
import { getRequiredAriaLabel } from 'util/form'
import { phoneFormat } from 'util/formatters'
import { SUPPORT_NUMBER } from 'util/support'
import Loading from '@ui-components-3/ui/lib/components/Loading'
import AngleRightIcon from '@ui-components-3/icons/lib/regular/AngleRight'
import ProviderInfoDialog from 'components/visit-creation/provider-info/ProviderInfoDialog'
import { clearProviders } from 'redux/slices/providers'
import { fetchAvailableProviders } from 'redux/thunks/providers'
import useId from '@ui-components-3/ui/lib/hooks/useId'
import { isSuccess } from 'util/helpers'

const renderProviderCards = ({
  labelId,
  list,
  handleProviderSelection,
  onDetailsClick,
  selectedProviderId,
  indexOffset = 0,
  isSuccess = false,
}) => (
  <RadioCardsGroup
    columnWidth={280}
    name="providerId"
    aria-labelledby={labelId}
    className="mt-xxs"
    options={list}
    controlPlacement="rt"
    onChange={(e) => handleProviderSelection(e.target.value)}
    value={selectedProviderId}
    getOptionValue={(provider) => provider.providerId}
    getLabelProps={(provider) => ({
      className: 'flex flex-col justify-between',
      suffix: (
        <button
          type="button"
          className="btn-small btn-neutral-borderless w-full justify-between rounded-none bg-neutral-200 text-neutral-600"
          onClick={onDetailsClick(provider)}
          aria-label={`See more details of ${getPrintableShortProviderName(provider, isSuccess)}`}
        >
          See more details
          <AngleRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      ),
    })}
    getOptionLabel={(provider) => {
      const index = list.indexOf(provider) + indexOffset
      return (
        <ProviderHeader
          provider={provider}
          data-testid={TestIds.newVisit.input.provider(index)}
          infoTestId={TestIds.newVisit.label.providerInfo(index)}
          isSuccess={isSuccess}
        />
      )
    }}
  />
)

// convert this file to typescript and fix the errors that come up
export const ProviderSelectionForm = withForm((props) => {
  const { isValid, handleSubmit, setFieldValue, pendingVisit, values } = props

  const successVisit = useMemo(() => {
    return isSuccess(pendingVisit.visitType)
  }, [pendingVisit])

  const isScheduledMedicalVisit = pendingVisit.scheduledType === 'scheduled' && pendingVisit.visitType === 'medical'

  const dispatch = useDispatch()

  const handleFetchAvailableProviders = useCallback(
    async (filters) => {
      if (isEmpty(pendingVisit)) return null
      dispatch(clearProviders())
      return dispatch(
        fetchAvailableProviders(
          pendingVisit.memberId,
          pendingVisit.visitType,
          pendingVisit.scheduledType,
          pendingVisit.location,
          filters,
          pendingVisit.locationCountry,
        ),
      )
    },
    [dispatch, pendingVisit],
  )

  const sectionedProviders = useSelector(ProviderSelectors.getSectionedAvailableProviders(pendingVisit))
  const allAvailableProviders = useSelector(ProviderSelectors.getAvailableProviders(pendingVisit))

  const [expandedProvider, setExpandedProvider] = useState(null)
  const providerInfoDialogRef = useRef(null)
  const [isFiltersVisible, setIsFiltersVisible] = useState(false)

  const toggleIsFiltersVisible = useCallback((_isVisible) => () => setIsFiltersVisible(!!_isVisible), [])

  const hasRecommendedProviders = useMemo(
    () => (sectionedProviders?.recommended?.length ?? 0) > 0,
    [sectionedProviders],
  )

  const defaultFilters = providerDefaultFilters(pendingVisit)
  useEffect(() => {
    if (!isEqual(values.filters, defaultFilters)) {
      const filters = normalizeFiltersForApi(values.filters)
      handleFetchAvailableProviders(filters)
    } else {
      handleFetchAvailableProviders()
    }
  }, [])

  useEffect(() => {
    if (!isEqual(values.filters, defaultFilters)) {
      const filters = normalizeFiltersForApi(values.filters)
      handleFetchAvailableProviders(filters)
      if (isEmpty(values.preferences)) {
        setFieldValue('preferences', normalizePreferencesForApi(filters))
      }
    }
  }, [values.filters])

  const handleProviderSelection = (providerId) => {
    setFieldValue('providerId', providerId)
    providerInfoDialogRef.current?.close()
    setExpandedProvider(null)
  }

  const handleProviderDetailsClick = useCallback(
    (provider) => (e) => {
      e.stopPropagation()
      if (!expandedProvider || expandedProvider.providerId !== provider.providerId) {
        setExpandedProvider(provider)
        providerInfoDialogRef.current?.open()
      }
    },
    [setExpandedProvider, expandedProvider],
  )

  useEffect(() => {
    setTimeout(() => {
      if (!values.providerId) {
        if (values.providerName) {
          setFieldValue('providerName', undefined)
        }
      } else {
        const provider = allAvailableProviders.find((provider) => provider.providerId === values.providerId)
        if (provider) {
          setFieldValue('providerName', getPrintableShortProviderName(provider, successVisit))
          setFieldValue('providerTimezone', provider.timezone)
        } else {
          console.error('no provider found')
        }
      }
    }, 100)
  }, [allAvailableProviders, setFieldValue, successVisit, values.providerId, values.providerName])

  const clearFilters = () => {
    setFieldValue('filters', defaultFilters)
    handleFetchAvailableProviders()
    toggleIsFiltersVisible(false)()
  }

  const applyFilters = (filters) => {
    setFieldValue('filters', filters)
    toggleIsFiltersVisible(false)()
  }

  const RenderFilters = useMemo(
    () =>
      isFiltersVisible
        ? pendingVisit && isTherapyRelated(pendingVisit)
          ? TherapyProviderFilters
          : NonTherapyProviderFilters
        : null,
    [isFiltersVisible, pendingVisit],
  )

  const filterButtons = [
    <button
      key="filter-btn"
      onClick={toggleIsFiltersVisible(true)}
      className="btn-small btn-primary-outlined"
      type="button"
    >
      <img src={FilterIconUrl} alt="" />
      Filter
    </button>,
  ]
  if (!isEmpty(values.filters) && !isEqual(values.filters, providerDefaultFilters(pendingVisit))) {
    filterButtons.push(
      <button
        key="filter-clear-btn"
        className="btn-small btn-primary-outlined ml-[10px]"
        onClick={clearFilters}
        type="button"
      >
        <img src={ResetIconUrl} alt="" />
        Clear
      </button>,
    )
  }

  const idPrefix = useId()
  const allProvidersLabelId = idPrefix + 'all'
  const recommendedProvidersLabelId = idPrefix + 'recommended'
  const availableProvidersLabelId = idPrefix + 'available'

  return (
    <>
      <form className="flex w-full flex-1 flex-col" onSubmit={handleSubmit}>
        <div className="max-w-[1180px] pb-6">
          <div className="mb-4 flex flex-col justify-between md:flex-row">
            <div className="mb-2 shrink-0">
              <h2
                className="typography-h5 m-0 text-xl text-neutral-800"
                data-testid={TestIds.newVisit.label.providerTitle}
                id={allProvidersLabelId}
              >
                Select {successVisit ? 'a coach' : 'an available provider'}
              </h2>
              {!hasRecommendedProviders && !isScheduledMedicalVisit && !successVisit && (
                <p className="typography-body font-medium text-neutral-600">Sorted by first available</p>
              )}
            </div>
            {/* TODO filters -> uncomment next line */}
            {/* <div className="flex flex-wrap items-center">{filterButtons}</div> */}
          </div>
          {allAvailableProviders.length === 0 ? (
            props.isLoading ? (
              <div className="flex justify-center">
                <Loading>
                  <p className="typography-body-l text-neutral">Locating Available Providers</p>
                </Loading>
              </div>
            ) : (
              <p className="typography-body-l text-neutral">No available provider matches your criteria.</p>
            )
          ) : (
            <div className="flex flex-col" aria-label={getRequiredAriaLabel('Provider')}>
              {hasRecommendedProviders && !successVisit && (
                <>
                  <span
                    className="typography-body font-medium uppercase text-neutral-600"
                    id={recommendedProvidersLabelId}
                  >
                    RECOMMENDED
                  </span>
                  <div data-testid={TestIds.newVisit.view.recommendedProviders}>
                    {renderProviderCards({
                      labelId: recommendedProvidersLabelId,
                      list: sectionedProviders.recommended,
                      handleProviderSelection,
                      onDetailsClick: handleProviderDetailsClick,
                      selectedProviderId: values.providerId,
                      indexOffset: 0,
                    })}
                  </div>
                  <span
                    className="typography-body pt-4 font-medium uppercase text-neutral-600"
                    id={availableProvidersLabelId}
                  >
                    AVAILABLE PROVIDERS
                  </span>
                </>
              )}
              <div data-testid={TestIds.newVisit.view.availableProviders}>
                {renderProviderCards({
                  labelId: hasRecommendedProviders ? availableProvidersLabelId : allProvidersLabelId,
                  list: !successVisit ? sectionedProviders?.general : allAvailableProviders,
                  handleProviderSelection,
                  onDetailsClick: handleProviderDetailsClick,
                  selectedProviderId: values.providerId,
                  indexOffset: sectionedProviders.recommended.length,
                  isSuccess: successVisit,
                })}
              </div>
            </div>
          )}
        </div>
        {!successVisit && (
          <div className="typography-body mb-6 font-medium text-neutral-800">
            Need help finding an appointment? Call{' '}
            <a className="text-link font-semibold" href={`tel:+${SUPPORT_NUMBER}`}>
              {phoneFormat(SUPPORT_NUMBER)}
            </a>{' '}
            to connect with our care coordinators.
          </div>
        )}
        {props.children({ disabled: !isValid || !values.providerId })}
      </form>
      <ProviderInfoDialog
        ref={providerInfoDialogRef}
        onSelect={() => handleProviderSelection(expandedProvider?.providerId)}
        provider={expandedProvider}
        isSuccess={successVisit}
        onClose={() => setExpandedProvider(null)}
      />
      {!!RenderFilters && (
        <RenderFilters
          onCancel={toggleIsFiltersVisible(false)}
          onSubmit={applyFilters}
          initialValues={values.filters || defaultFilters}
          schema={FiltersSchema}
        />
      )}
    </>
  )
})

ProviderSelectionForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  enableReinitialize: PropTypes.bool,
  dirty: PropTypes.bool,
  isValid: PropTypes.bool,
  isLoading: PropTypes.bool,
  pendingVisit: PropTypes.object,
  children: PropTypes.func.isRequired,
}

export default ProviderSelectionForm
